import React from 'react';
import PropTypes from 'prop-types';
import { truncate } from 'lodash';
import cx from 'classnames';
import Heading from '../Heading';
import FauxImage from '../FauxImage';
import { useWindowSize } from '../../../hooks';
import { ExternalLink } from '../../sharedComponents/Link';

export default function InventoryPromoLinkTall({
    className,
    href,
    headingLevel,
    maxHeadingLength,
    maxCopyLength,
    imagePath,
    mobileImagePath,
    imageAlt,
    listItems,
    heading,
    copy,
    children,
    height,
    headingPosition,
    buttonOnClick,
    hasReducedHeightForMobile,
    isExternalLink,
    ...rest
}) {
    const { width } = useWindowSize();

    let promoHasCopy = '';
    if (copy && copy.length > 1) {
        promoHasCopy = 'sarsa-inventory-promo-tall-has-copy';
    }

    let headingClasses = cx('sarsa-inventory-promo-tall-heading-wrap', {
        'sarsa-align-heading-bottom': headingPosition === 'headingBottom',
        'sarsa-align-heading-center': headingPosition === 'headingCenter',
        'sarsa-align-heading-top': headingPosition === 'headingTop',
    });

    let fauxImageHeight = cx({
        '240px': height === 'tall',
        '320px': height === 'taller',
        '400px': height === 'tallest',
    });

    let reducedHeightForMobile = '';
    // Reduce the height of the promo for mobile.
    if (width <= 768 && hasReducedHeightForMobile) {
        reducedHeightForMobile = 'sarsa-promo-reduced-height';

        // Reduce the height of the faux image to 90px
        fauxImageHeight = cx({
            '90px': height === 'tall' || 'taller' || 'tallest'
        });

        // Center align the heading
        headingClasses = cx('sarsa-inventory-promo-tall-heading-wrap', {
            'sarsa-align-heading-center': headingPosition === 'headingCenter' || 'sarsa-align-heading-top' || 'sarsa-align-heading-bottom',
        });
    }

    // Main content for the tall promo
    const promoBody = (
        <React.Fragment>
            <div className="sarsa-inventory-promo-tall-image-heading-wrap">
                <div className={headingClasses}>
                    <Heading headingLevel={headingLevel} appearance="h3">
                        {truncate(heading, { length: maxHeadingLength })}
                    </Heading>
                    {listItems && listItems.length > 1 && (
                        <div className="sarsa-tall-inventory-promo-tall-list-items">
                            {listItems.map((listItem, index) => (
                                <span key={index}>{listItem}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="sarsa-inventory-promo-tall-image-wrap">
                    <FauxImage
                        role="presentation"
                        src={imagePath}
                        mobileSrc={mobileImagePath}
                        altText={imageAlt}
                        style={{ minHeight: `${fauxImageHeight}` }}
                    />
                    <div className="sarsa-tall-carousel-overlay--on-hover" />
                </div>
            </div>

            {/* Show copy if it exists when the viewport is greater than 768px */}
            {copy && copy.length > 1 && width >= 768 && (
                <div className="sarsa-inventory-promo-tall-content-wrap">
                    <p>{truncate(copy, { length: maxCopyLength })}</p>
                </div>
            )}
            {children}
        </React.Fragment>
    );

    const defaultTallPromoStyles = `sarsa-inventory-promo-link-tall-wrap ${promoHasCopy} ${reducedHeightForMobile} ${className}`;
    
    // If the promo is a an external link, set the prop `isExternalLink` = true.
    if (href && isExternalLink) {
        return (
            <ExternalLink
                url={href}
                className={defaultTallPromoStyles}
                {...rest}
            >
                {promoBody}
            </ExternalLink>
        )
    }
    // If the promo is a link, a href will be defined and the promo will be wrapped in an <a> tag.
    else if (href) {
        return (
            <a
                href={href}
                className={defaultTallPromoStyles}
                {...rest}
            >
                {promoBody}
            </a>
        );
    }
    return (
        <button
            onClick={buttonOnClick}
            className={defaultTallPromoStyles}
            {...rest}
        >
            {promoBody}
        </button>
    );
}

InventoryPromoLinkTall.propTypes = {
    /** Path for the promo image.   */
    imagePath: PropTypes.string.isRequired,

    /** Path for the mobile version of the promo image.  */
    mobileImagePath: PropTypes.string,

    /** href for the promo.   */
    href: PropTypes.string,

    /** Heading for the promo.   */
    heading: PropTypes.string.isRequired,

    /** <h#> for the promo.   */
    headingLevel: PropTypes.number,

    /** Set a character count for the promo heading.   */
    maxHeadingLength: PropTypes.number,

    /** Alt text for the promo image.   */
    imageAlt: PropTypes.string,

    /** List items for the promo.   */
    listItems: PropTypes.array,

    /** Copy for the promo.   */
    copy: PropTypes.string,

    /** Set a character count for the promo copy.   */
    maxCopyLength: PropTypes.number,

    /** Sets the height of the promo item.   */
    height: PropTypes.oneOf(['tall', 'taller', 'tallest']),

    /** Sets the height of the promo item.   */
    headingPosition: PropTypes.oneOf(['headingBottom', 'headingCenter', 'headingTop']),

    /** Additional className that can be added to the component wrapper.   */
    className: PropTypes.string,

    /** If the promo needs to be a button, a click function can be set here.   */
    buttonOnClick: PropTypes.func,

    /** Reduce the height of the promo when the viewport is below 768px   */
    hasReducedHeightForMobile: PropTypes.bool,

    /** Is the link for the promo pointing to an external source  */
    isExternalLink: PropTypes.bool,

    setMarginBottom: PropTypes.string,

    /** Add additional content to the component if necessary.  */
    children: PropTypes.node,
};

InventoryPromoLinkTall.defaultProps = {
    headingLevel: 3,
    maxHeadingLength: 43,
    maxCopyLength: 110,
    className: '',
    height: 'tall',
    headingPosition: 'headingCenter',
    buttonOnClick: null,
    hasReducedHeightForMobile: false,
    isExternalLink: false
};
